import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaHandPointer } from "react-icons/fa";
import greenojLogo from "../../images/greenoj.png";
import gubcpcLogo from "../../images/gubcpc-contest.png";

const departments = ["CSE", "SWE", "ADS", "EEE", "TEX", "Others"];
const paymentWays = ["bkash", "nagad", "rocket"];
const contestDate = new Date("2026-07-08T10:30:00+06:00");
const registrationDeadline = new Date("2026-07-07T23:59:00+06:00");
const rulebookUrl = "https://drive.google.com/file/d/YOUR_FILE_ID/view?usp=sharing";
const paymentNumbers = [
  { label: "bKash Personal", number: "01879597656" },
  { label: "Nagad Personal", number: "01879597656" },
  { label: "Rocket Personal", number: "01879597656" },
];

const initialForm = {
  teamName: "",
  leaderName: "",
  leaderPhone: "",
  leaderEmail: "",
  leaderStudentId: "",
  leaderDepartment: "CSE",
  secondMemberName: "",
  secondMemberPhone: "",
  secondMemberEmail: "",
  secondMemberStudentId: "",
  secondMemberDepartment: "CSE",
  transactionId: "",
  paymentNumber: "",
  paymentWay: "bkash",
};

const panelClass = "home-card-strong p-6 sm:p-8 lg:p-10 mb-6 sm:mb-8";
const mutedTextClass = "home-muted";
const primaryButtonClass = "home-btn-primary px-6 py-2.5 text-sm font-semibold sm:px-8 sm:py-3 sm:text-base cursor-pointer";
const secondaryButtonClass = "home-btn-secondary px-6 py-2.5 text-sm font-medium sm:px-8 sm:py-3 sm:text-base cursor-pointer";
const inputClass = "w-full rounded-xl border border-[var(--border)] bg-[var(--surface-soft)] px-4 py-3 text-sm text-[var(--text)] outline-none transition-all duration-300 placeholder:text-[#94A3B8]/60 focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)]";
const sectionTitleClass = "home-title mt-4 text-2xl font-bold sm:text-4xl text-[var(--text)]";
const bodyTextClass = "text-sm sm:text-base home-muted leading-relaxed";

function formatDate(date) {
  return new Intl.DateTimeFormat("en-BD", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === "localhost" ? "http://localhost:5000" : "https://api.gubcpc.com");

export default function JulyMemorialContest() {
  const location = useLocation();
  const navigate = useNavigate();

  const view = useMemo(() => {
    const path = location.pathname;
    if (path.endsWith("/registration")) return "register";
    if (path.endsWith("/status")) return "status";
    return "home";
  }, [location.pathname]);

  const handleNavigate = (nextView) => {
    if (nextView === "home") {
      navigate("/events/july-memorial-programming-contest-2026");
    } else {
      navigate(`/events/july-memorial-programming-contest-2026/${nextView}`);
    }
  };

  const [form, setForm] = useState(initialForm);
  const [registerStep, setRegisterStep] = useState(1);
  const [notice, setNotice] = useState("");
  const [errors, setErrors] = useState({});
  const [lookupEmail, setLookupEmail] = useState("");
  const [results, setResults] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const [timeLeft, setTimeLeft] = useState(getTimeLeft(contestDate));
  const [registrationTimeLeft, setRegistrationTimeLeft] = useState(
    getTimeLeft(registrationDeadline),
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTimeLeft(getTimeLeft(contestDate));
      setRegistrationTimeLeft(getTimeLeft(registrationDeadline));
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    setNotice("");
    setErrors({});
  }, [view]);

  const stats = useMemo(
    () => [
      { label: "Contest Date", value: "08 July 2026" },
      { label: "Contest Duration", value: "3 Hours" },
      { label: "Team Size", value: "2 Per Team" },
      { label: "Registration Fee", value: "99 Taka" },
    ],
    [],
  );

  const highlights = [
    "ICPC-style contest format with 3 hours of intense algorithmic problem solving.",
    "Teams of 2 members competing together on GreenOJ.",
    "Prizepool to be announced soon.",
    "Snacks for participants during the event.",
  ];
  const registerSteps = [
    { id: 1, title: "Team Info", note: "Start with your team identity." },
    { id: 2, title: "Team Leader", note: "Primary contact and captain details." },
    { id: 3, title: "2nd Member", note: "Add the second teammate information." },
    { id: 4, title: "Payment Info", note: "Confirm payment details to finish." },
  ];

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const stepFields = {
    1: ["teamName"],
    2: [
      "leaderName",
      "leaderPhone",
      "leaderEmail",
      "leaderStudentId",
      "leaderDepartment",
    ],
    3: [
      "secondMemberName",
      "secondMemberPhone",
      "secondMemberEmail",
      "secondMemberStudentId",
      "secondMemberDepartment",
    ],
    4: ["paymentWay", "transactionId", "paymentNumber"],
  };

  const handleNextStep = () => {
    const newErrors = {};

    // Check all required fields on the current step
    stepFields[registerStep].forEach((field) => {
      if (String(form[field] || "").trim() === "") {
        newErrors[field] = "This field is required.";
      }
    });

    // Validate specific inputs per step
    if (registerStep === 2) {
      if (form.leaderEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.leaderEmail)) {
        newErrors.leaderEmail = "Please enter a valid Team Leader email address.";
      }
      if (form.leaderStudentId && !/^\d{9}$/.test(form.leaderStudentId)) {
        newErrors.leaderStudentId = "Leader Student ID must be exactly 9 digits.";
      }
      if (form.leaderPhone && !/^\d{11}$/.test(form.leaderPhone)) {
        newErrors.leaderPhone = "Leader Phone Number must be exactly 11 digits.";
      }
    }
    if (registerStep === 3) {
      if (form.secondMemberEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.secondMemberEmail)) {
        newErrors.secondMemberEmail = "Please enter a valid 2nd Member email address.";
      }
      if (form.secondMemberStudentId && !/^\d{9}$/.test(form.secondMemberStudentId)) {
        newErrors.secondMemberStudentId = "Second Member Student ID must be exactly 9 digits.";
      }
      if (form.secondMemberPhone && !/^\d{11}$/.test(form.secondMemberPhone)) {
        newErrors.secondMemberPhone = "Second Member Phone Number must be exactly 11 digits.";
      }
      if (
        form.leaderEmail &&
        form.secondMemberEmail &&
        form.leaderEmail.trim().toLowerCase() === form.secondMemberEmail.trim().toLowerCase()
      ) {
        newErrors.secondMemberEmail = "2nd member email must be different from the team leader email.";
      }
      if (
        form.leaderPhone &&
        form.secondMemberPhone &&
        form.leaderPhone.trim() === form.secondMemberPhone.trim()
      ) {
        newErrors.secondMemberPhone = "2nd member phone must be different from the team leader phone.";
      }
      if (
        form.leaderStudentId &&
        form.secondMemberStudentId &&
        form.leaderStudentId.trim() === form.secondMemberStudentId.trim()
      ) {
        newErrors.secondMemberStudentId = "2nd member student ID must be different from the team leader student ID.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    setNotice("");
    setRegisterStep((current) => Math.min(registerSteps.length, current + 1));
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (registerStep < registerSteps.length) {
      handleNextStep();
      return;
    }

    // Step 4 validation
    const newErrors = {};
    stepFields[4].forEach((field) => {
      if (String(form[field] || "").trim() === "") {
        newErrors[field] = "This field is required.";
      }
    });

    if (
      form.leaderEmail.trim().toLowerCase() === form.secondMemberEmail.trim().toLowerCase()
    ) {
      newErrors.secondMemberEmail = "2nd member email must be different from the team leader email.";
    }
    if (form.leaderPhone.trim() === form.secondMemberPhone.trim()) {
      newErrors.secondMemberPhone = "2nd member phone must be different from the team leader phone.";
    }
    if (form.leaderStudentId.trim() === form.secondMemberStudentId.trim()) {
      newErrors.secondMemberStudentId = "2nd member student ID must be different from the team leader student ID.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    setNotice("");

    try {
      const res = await fetch(`${API_BASE_URL}/student/july-contest/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const resData = await res.json();
      if (!res.ok) {
        throw new Error(resData.message || "Failed to submit registration");
      }

      setNotice("Registration submitted successfully. Payment verification is pending.");
      setLookupEmail(form.leaderEmail);
      setResults([resData.registration]);
      setForm(initialForm);
      setErrors({});
      setRegisterStep(1);
      handleNavigate("status");
    } catch (err) {
      setNotice(err.message || "An error occurred during submission.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLookup = async (event) => {
    event.preventDefault();
    if (!lookupEmail.trim()) {
      setNotice("Please enter your team leader's email address.");
      return;
    }

    setLoadingStatus(true);
    setNotice("");
    setResults([]);

    try {
      const res = await fetch(`${API_BASE_URL}/student/july-contest/status?email=${encodeURIComponent(lookupEmail.trim())}`);
      const resData = await res.json();

      if (!res.ok) {
        throw new Error(resData.message || "Failed to fetch status");
      }

      const matches = resData.data || [];
      setResults(matches);
      setNotice(
        matches.length
          ? `Found ${matches.length} registration${matches.length > 1 ? "s" : ""}.`
          : "No registration found for that team leader email address yet."
      );
    } catch (err) {
      setNotice(err.message || "An error occurred while checking registration.");
    } finally {
      setLoadingStatus(false);
    }
  };

  return (
    <main className="home-shell min-h-screen pt-24 pb-12">
      {/* Contest header section with tab controller */}
      <div className="home-section pb-2 border-none">
        <div className="home-container">
          <div className="home-card-strong p-8 sm:p-10 mb-8">
            <span className="section-kicker">Memorial Edition</span>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <h1 className="home-title text-3xl font-bold sm:text-5xl">
                  July Memorial Programming Contest 2026
                </h1>
                <p className="mt-4 max-w-3xl text-sm sm:text-base home-muted">
                  Honoring the spirit of the July Uprising with problem-solving, algorithmic thinking, and collective purpose.
                </p>
              </div>
              <div className="home-card inline-flex p-1 w-fit self-start md:self-end">
                <button
                  type="button"
                  onClick={() => handleNavigate("home")}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition cursor-pointer ${
                    view === "home" ? "home-btn-primary" : "home-muted"
                  }`}
                >
                  Overview
                </button>
                <button
                  type="button"
                  onClick={() => handleNavigate("registration")}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition cursor-pointer ${
                    view === "register" ? "home-btn-primary" : "home-muted"
                  }`}
                >
                  Register
                </button>
                <button
                  type="button"
                  onClick={() => handleNavigate("status")}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition cursor-pointer ${
                    view === "status" ? "home-btn-primary" : "home-muted"
                  }`}
                >
                  Status Check
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {view === "home" && (
        <div className="home-container px-4 sm:px-6">
          <section className={`${panelClass} grid gap-6 md:grid-cols-[1.5fr_1fr] md:gap-8`}>
            <div>
              <span className="section-kicker mb-3">CONTEST OVERVIEW</span>
              <h2 className={sectionTitleClass}>Code with memory, courage, and purpose.</h2>
              <p className={`mt-6 ${bodyTextClass}`}>
                The July Memorial Programming Contest 2026 brings together problem solvers across departments to compete in a spirit of resistance, collaboration, and academic growth.
              </p>
              <div className="mt-6 rounded-2xl border border-[var(--border)] bg-[var(--surface-soft)] p-5">
                <p className="text-xs font-bold uppercase tracking-[0.22em] home-accent">
                  Surprise Highlight
                </p>
                <p className="mt-2 text-xl font-bold tracking-tight text-[var(--text)] sm:text-2xl">
                  2 Mystery Gifts
                </p>
                <p className="mt-2 text-sm leading-relaxed home-muted">
                  Two random participants will receive special mystery gifts during the contest event.
                </p>
              </div>
              <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap sm:gap-4">
                <button
                  type="button"
                  className={primaryButtonClass}
                  onClick={() => handleNavigate("registration")}
                >
                  Register for the Contest <FaHandPointer className="inline-block text-2xl sm:text-3xl ml-3 align-middle text-yellow-400" />
                </button>
                <a
                  className={secondaryButtonClass}
                  href={rulebookUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Download Rulebook
                </a>
              </div>
            </div>

            <div className="relative rounded-2xl border border-[var(--border)] bg-[var(--surface-soft)] p-5 sm:p-6">
              <div className="space-y-6">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] home-muted">
                    Registration Deadline
                  </div>
                  <div className="mt-1 text-lg font-bold tracking-tight text-[var(--text)] sm:text-xl">
                    07 July 2026, 11:59 PM
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4">
                  <CountdownBox label="Days" value={registrationTimeLeft.days} />
                  <CountdownBox label="Hours" value={registrationTimeLeft.hours} />
                  <CountdownBox label="Minutes" value={registrationTimeLeft.minutes} />
                  <CountdownBox label="Seconds" value={registrationTimeLeft.seconds} />
                </div>

                <div className="h-px bg-[var(--border)]" />

                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] home-muted">
                    Contest Starts
                  </div>
                  <div className="mt-1 text-lg font-bold tracking-tight text-[var(--text)] sm:text-xl">
                    {formatDate(contestDate)}
                  </div>
                  <p className="mt-1 text-xs font-medium uppercase tracking-[0.18em] home-muted opacity-80">
                    Time may change slightly
                  </p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4">
                <CountdownBox label="Days" value={timeLeft.days} />
                <CountdownBox label="Hours" value={timeLeft.hours} />
                <CountdownBox label="Minutes" value={timeLeft.minutes} />
                <CountdownBox label="Seconds" value={timeLeft.seconds} />
              </div>
            </div>
          </section>

          <section className="mb-6 grid gap-4 sm:grid-cols-2 lg:mb-8 lg:grid-cols-4 lg:gap-5">
            {stats.map((item) => (
              <article className="home-card p-5" key={item.label}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] home-muted">
                  {item.label}
                </p>
                <strong className="mt-2 block text-2xl font-bold tracking-tight text-[var(--text)] sm:text-3xl">
                  {item.value}
                </strong>
              </article>
            ))}
          </section>

          <section className={`${panelClass} grid gap-6 md:grid-cols-[1.2fr_0.8fr] md:gap-8`}>
            <div>
              <span className="section-kicker mb-3">HISTORY & CONTEXT</span>
              <h2 className={sectionTitleClass}>Remembering a movement that changed Bangladesh.</h2>
              <p className={bodyTextClass}>
                The July Uprising began in June 2024 as student protests against controversial quota policies and rapidly grew into a national pro-democracy movement. When violence, shutdowns, and blackouts followed, public resistance only strengthened.
              </p>
              <p className={`mt-5 ${bodyTextClass}`}>
                After the widely remembered July Massacre and the deaths of unarmed students including Abu Sayed, the movement expanded far beyond campuses. On 5 August 2024, millions joined a final wave of defiance that forced the resignation of the prime minister, ending a 15-year rule.
              </p>
            </div>
            <div className="grid gap-4">
              {[
                {
                  date: "June 2024",
                  desc: "Student protests ignite around quota reform and democratic demands.",
                },
                {
                  date: "Mid-July 2024",
                  desc: "Violent crackdown, nationwide outrage, and a defining moment of sacrifice.",
                },
                {
                  date: "05 August 2024",
                  desc: "Mass non-cooperation peaks, the regime collapses, and a new chapter begins.",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-[var(--border)] bg-[var(--surface-soft)] p-5"
                >
                  <span className="font-display text-xl font-bold tracking-tight home-accent">
                    {item.date}
                  </span>
                  <p className="mt-2 text-sm leading-relaxed home-muted">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="grid gap-6 md:grid-cols-2 lg:gap-8">
            <div className="home-card p-6 sm:p-8">
              <span className="section-kicker mb-3">CONTEST INFORMATION</span>
              <h2 className={sectionTitleClass}>Official contest details</h2>
              <ul className="list-disc space-y-4 pl-5 text-sm sm:text-base home-muted marker:text-[var(--brand)] mt-4">
                <li>Official name: GUBCPC Presents July Memorial Programming Contest 2026</li>
                <li>Date: 08 July 2026</li>
                <li>Contest duration: 3 hours</li>
                <li>Team size: 2 per team</li>
                <li>Prizepool: TBA</li>
                <li>Registration fee: 99 taka per team</li>
                <li>Eligibility: all students except the advance batch of GUBCPC</li>
              </ul>
            </div>

            <div className="home-card p-6 sm:p-8">
              <span className="section-kicker mb-3">WHAT TO EXPECT</span>
              <h2 className={sectionTitleClass}>Built for focused competitive programming.</h2>
              <ul className="list-disc space-y-4 pl-5 text-sm sm:text-base home-muted marker:text-[var(--brand)] mt-4">
                {highlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <LogoCard src={greenojLogo} title="Platform" label="GreenOJ" />
                <LogoCard src={gubcpcLogo} title="Organizer" label="GUB CPC" />
              </div>
            </div>
          </section>

          <section className="home-card p-6 sm:p-8 mt-6 sm:mt-8">
            <span className="section-kicker mb-3">PROBLEM SETTERS</span>
            <h2 className={sectionTitleClass}>Crafted by the contest setters.</h2>
            <div className="grid gap-4 mt-6 sm:grid-cols-2 xl:grid-cols-5">
              {[
                "Ismatul Islam Pranto",
                "Md Hosain Rohman Noyon",
                "Ruhan Azad",
                "Mahfuz Bin Minhaz",
                "Md. Asif Samrat",
                "Md Din Islam",
              ].map((setter) => (
                <div
                  key={setter}
                  className="rounded-xl border border-[var(--border)] bg-[var(--surface-soft)] px-4 py-5 text-center"
                >
                  <p className="font-display text-base font-bold tracking-tight text-[var(--text)]">
                    {setter}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {view === "register" && (
        <div className="home-container px-4 sm:px-6">
          <section className={`${panelClass} max-w-5xl mx-auto`}>
            <span className="section-kicker mb-3">CONTEST REGISTRATION</span>
            <h2 className={sectionTitleClass}>Register your team</h2>
            <p className={`mb-8 mt-2 ${bodyTextClass}`}>
              Submit team details, payment information, and your transaction ID.
            </p>

            {notice ? (
              <div className="mb-6 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-amber-500">
                  {notice}
                </p>
              </div>
            ) : null}

            <div className="mb-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {registerSteps.map((step) => {
                const isActive = registerStep === step.id;
                const isDone = registerStep > step.id;

                return (
                  <div
                    key={step.id}
                    className={`rounded-xl border px-4 py-4 transition-all duration-300 ${
                      isActive
                        ? "border-[var(--brand)] bg-[var(--brand-soft)]/20"
                        : isDone
                          ? "border-emerald-500/30 bg-emerald-500/5"
                          : "border-[var(--border)] bg-[var(--surface-soft)]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                          isActive
                            ? "bg-[var(--accent)] text-[var(--accent-contrast)]"
                            : isDone
                              ? "bg-emerald-500 text-white"
                              : "bg-[var(--border)] text-[var(--text)]"
                        }`}
                      >
                        {step.id}
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--text)] sm:text-sm">
                          {step.title}
                        </p>
                        <p className="text-xs home-muted">{step.note}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <form className="grid gap-6" onSubmit={handleSubmit}>
              {registerStep === 1 && (
                <div className="grid gap-5 md:grid-cols-2">
                  <Input
                    label="Team Name"
                    name="teamName"
                    value={form.teamName}
                    onChange={handleChange}
                    placeholder="Example: Algorithmic Avengers"
                    hint="A formal and beautiful team name is recommended."
                    error={errors.teamName}
                  />
                </div>
              )}

              {registerStep === 2 && (
                <div className="grid gap-5 md:grid-cols-2">
                  <Input
                    label="Team Leader Name"
                    name="leaderName"
                    value={form.leaderName}
                    onChange={handleChange}
                    placeholder="Full name of the team leader"
                    error={errors.leaderName}
                  />
                  <Input
                    label="Leader Phone No"
                    name="leaderPhone"
                    value={form.leaderPhone}
                    onChange={handleChange}
                    placeholder="01XXXXXXXXX"
                    error={errors.leaderPhone}
                  />
                  <Input
                    label="Leader Email"
                    type="email"
                    name="leaderEmail"
                    value={form.leaderEmail}
                    onChange={handleChange}
                    placeholder="leader@example.com"
                    error={errors.leaderEmail}
                  />
                  <Input
                    label="Leader Student ID"
                    name="leaderStudentId"
                    value={form.leaderStudentId}
                    onChange={handleChange}
                    placeholder="Student ID (9 digits)"
                    error={errors.leaderStudentId}
                  />
                  <Select
                    label="Leader Department"
                    name="leaderDepartment"
                    value={form.leaderDepartment}
                    onChange={handleChange}
                    options={departments}
                    error={errors.leaderDepartment}
                  />
                </div>
              )}

              {registerStep === 3 && (
                <div className="grid gap-5 md:grid-cols-2">
                  <Input
                    label="2nd Member Name"
                    name="secondMemberName"
                    value={form.secondMemberName}
                    onChange={handleChange}
                    placeholder="Full name of the second member"
                    error={errors.secondMemberName}
                  />
                  <Input
                    label="2nd Member Phone No"
                    name="secondMemberPhone"
                    value={form.secondMemberPhone}
                    onChange={handleChange}
                    placeholder="01XXXXXXXXX"
                    error={errors.secondMemberPhone}
                  />
                  <Input
                    label="2nd Member Email"
                    type="email"
                    name="secondMemberEmail"
                    value={form.secondMemberEmail}
                    onChange={handleChange}
                    placeholder="member2@example.com"
                    error={errors.secondMemberEmail}
                  />
                  <Input
                    label="2nd Member Student ID"
                    name="secondMemberStudentId"
                    value={form.secondMemberStudentId}
                    onChange={handleChange}
                    placeholder="Student ID (9 digits)"
                    error={errors.secondMemberStudentId}
                  />
                  <Select
                    label="2nd Member Department"
                    name="secondMemberDepartment"
                    value={form.secondMemberDepartment}
                    onChange={handleChange}
                    options={departments}
                    error={errors.secondMemberDepartment}
                  />
                </div>
              )}

              {registerStep === 4 && (
                <div className="grid gap-6">
                  <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-soft)] p-5">
                    <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--text)]">
                      Send 99 taka to any one of these numbers
                    </p>
                    <div className="mt-4 grid gap-3 lg:grid-cols-3">
                      {paymentNumbers.map((item) => (
                        <div
                          key={item.label}
                          className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4"
                        >
                          <p className="text-xs font-bold uppercase tracking-[0.14em] home-muted">
                            {item.label}
                          </p>
                          <p className="mt-2 text-lg font-semibold text-[var(--text)]">
                            {item.number}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-5 md:grid-cols-2">
                    <Select
                      label="Payment Way"
                      name="paymentWay"
                      value={form.paymentWay}
                      onChange={handleChange}
                      options={paymentWays}
                      error={errors.paymentWay}
                    />
                    <Input
                      label="Transaction ID"
                      name="transactionId"
                      value={form.transactionId}
                      onChange={handleChange}
                      placeholder="Enter the transaction ID"
                      error={errors.transactionId}
                    />
                    <Input
                      label="Payment Sender Mobile Number"
                      name="paymentNumber"
                      value={form.paymentNumber}
                      onChange={handleChange}
                      placeholder="The number used to send the payment"
                      error={errors.paymentNumber}
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-4 border-t border-[var(--border)] pt-5 sm:flex-row sm:flex-wrap sm:items-center">
                <a
                  className={secondaryButtonClass}
                  href={rulebookUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Download Rulebook
                </a>
                <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:ml-auto">
                  {registerStep > 1 ? (
                    <button
                      type="button"
                      className={secondaryButtonClass}
                      onClick={() =>
                        setRegisterStep((current) => Math.max(1, current - 1))
                      }
                    >
                      Previous
                    </button>
                  ) : null}

                  {registerStep < registerSteps.length ? (
                    <button
                      type="button"
                      className={primaryButtonClass}
                      onClick={() => handleNextStep()}
                    >
                      Next Step
                    </button>
                  ) : (
                    <button type="submit" disabled={submitting} className={primaryButtonClass}>
                      {submitting ? "Submitting..." : "Submit Registration"}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </section>
        </div>
      )}

      {view === "status" && (
        <div className="home-container px-4 sm:px-6">
          <section className={`${panelClass} max-w-5xl mx-auto`}>
            <span className="section-kicker mb-3">PAYMENT STATUS</span>
            <h2 className={sectionTitleClass}>Check your registration</h2>
            <p className={`mb-8 mt-2 ${bodyTextClass}`}>
              Search using the team leader email address used during registration.
            </p>

            <form
              className="grid items-center gap-3 sm:grid-cols-[minmax(0,24rem)_max-content] sm:gap-4"
              onSubmit={handleLookup}
            >
              <input
                type="email"
                placeholder="teamleader@example.com"
                value={lookupEmail}
                onChange={(event) => setLookupEmail(event.target.value)}
                className={inputClass}
                required
              />
              <button type="submit" disabled={loadingStatus} className={primaryButtonClass}>
                {loadingStatus ? "Searching..." : "Check Status"}
              </button>
            </form>

            {notice ? (
              <div className="mt-6 rounded-xl bg-amber-500/5 border border-amber-500/20 p-4">
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-amber-500">
                  {notice}
                </p>
              </div>
            ) : null}

            <div className="mt-8 grid gap-4 sm:gap-5">
              {results.map((entry) => (
                <article
                  className="grid gap-4 rounded-2xl border border-[var(--border)] bg-[var(--surface-soft)] p-5 transition-all duration-300 hover:bg-[var(--surface)] sm:p-6 md:grid-cols-2 lg:grid-cols-3"
                  key={entry.id}
                >
                  <div>
                    <span className={`block text-xs uppercase tracking-[0.16em] ${mutedTextClass}`}>
                      Team
                    </span>
                    <strong className="text-lg text-[var(--text)]">
                      {entry.teamName}
                    </strong>
                  </div>
                  <div>
                    <span className={`block text-xs uppercase tracking-[0.16em] ${mutedTextClass}`}>
                      Leader
                    </span>
                    <strong className="text-lg text-[var(--text)]">
                      {entry.leaderName}
                    </strong>
                  </div>
                  <div>
                    <span className={`block text-xs uppercase tracking-[0.16em] ${mutedTextClass} mb-2`}>
                      Payment Status
                    </span>
                    <strong className={`inline-flex w-fit rounded-full px-4 py-1 text-sm font-semibold border ${
                      entry.status === "Verified"
                        ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/35 dark:text-emerald-400"
                        : entry.status === "Rejected"
                          ? "bg-rose-500/10 text-rose-600 border-rose-500/35 dark:text-rose-400"
                          : "bg-amber-500/10 text-amber-600 border-amber-500/35 dark:text-amber-400"
                    }`}>
                      {entry.status}
                    </strong>
                  </div>
                  <div>
                    <span className={`block text-xs uppercase tracking-[0.16em] ${mutedTextClass}`}>
                      Payment Method
                    </span>
                    <strong className="text-lg text-[var(--text)] uppercase">
                      {entry.paymentWay}
                    </strong>
                  </div>
                  <div>
                    <span className={`block text-xs uppercase tracking-[0.16em] ${mutedTextClass}`}>
                      Submitted
                    </span>
                    <strong className="text-lg text-[var(--text)]">
                      {formatDate(new Date(entry.createdAt || entry.submittedAt))}
                    </strong>
                  </div>
                  {entry.remarks ? (
                    <div className="col-span-full border-t border-[var(--border)] pt-4">
                      <span className={`block text-xs uppercase tracking-[0.16em] ${mutedTextClass}`}>
                        Remarks / Feedbacks
                      </span>
                      <p className="mt-2 text-sm text-amber-600 dark:text-amber-400 font-medium bg-amber-500/5 rounded-xl p-3 border border-amber-500/15">
                        {entry.remarks}
                      </p>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

function CountdownBox({ label, value }) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3 py-4 text-center transition-all duration-300 hover:border-[var(--brand)]">
      <strong className="block text-2xl sm:text-3xl font-bold leading-none text-[var(--text)]">
        {String(value).padStart(2, "0")}
      </strong>
      <span className="mt-2 block text-[0.65rem] font-semibold uppercase tracking-[0.22em] home-muted">
        {label}
      </span>
    </div>
  );
}

function LogoCard({ src, title, label }) {
  const imageClass =
    label === "GUB CPC"
      ? "h-[64px] w-[64px] object-contain"
      : "h-[44px] w-[44px] object-contain";

  return (
    <div className="group relative overflow-hidden flex items-center gap-5 rounded-2xl border border-[var(--border)] bg-[var(--surface-soft)] p-4 transition-all duration-300 hover:bg-[var(--surface)]">
      <div className="relative">
        <img className={imageClass} src={src} alt={label} />
      </div>
      <div className="relative">
        <span className="block text-[0.65rem] font-semibold uppercase tracking-[0.18em] home-muted">
          {title}
        </span>
        <strong className="block text-base tracking-tight text-[var(--text)] leading-tight">
          {label}
        </strong>
      </div>
    </div>
  );
}

function Input({ label, hint, error, ...props }) {
  return (
    <label className="grid gap-2.5">
      <span className="text-[0.75rem] font-bold uppercase tracking-[0.16em] text-[var(--text)]">
        {label}
      </span>
      <input
        className={`${inputClass} ${
          error ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500/20" : ""
        }`}
        required
        {...props}
      />
      {error ? (
        <span className="text-xs font-semibold text-rose-500">{error}</span>
      ) : hint ? (
        <span className="text-xs home-muted">{hint}</span>
      ) : null}
    </label>
  );
}

function Select({ label, name, value, onChange, options, error }) {
  return (
    <label className="grid gap-2.5">
      <span className="text-[0.75rem] font-bold uppercase tracking-[0.16em] text-[var(--text)]">
        {label}
      </span>
      <select
        className={`${inputClass} ${
          error ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500/20" : ""
        }`}
        name={name}
        value={value}
        onChange={onChange}
        required
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {error ? (
        <span className="text-xs font-semibold text-rose-500">{error}</span>
      ) : null}
    </label>
  );
}

function getTimeLeft(targetDate) {
  const distance = targetDate.getTime() - Date.now();

  if (distance <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    days: Math.floor(distance / (1000 * 60 * 60 * 24)),
    hours: Math.floor((distance / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((distance / (1000 * 60)) % 60),
    seconds: Math.floor((distance / 1000) % 60),
  };
}
